import type { Group } from '@gik/core/models/gik/Group';
import routes from '@gik/core/routes';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { createPageGroupIdParameter } from '@gik/create/components/CreatePage/CreatePage';
import { Button } from '@gik/ui-legacy/Button';
import DocumentAddIcon from '@heroicons/react/outline/DocumentAddIcon';
import DocumentIcon from '@heroicons/react/outline/DocumentIcon';
import type React from 'react';
import GroupsIcon from '../assets/groups-icon.svg';
import { openAddInkindPagesModal } from '../utils/openAddInkindPagesModal.tsx';

export type InkindGroupEmptyViewProps = {
  group: Group;
  search: string;
  /**
   * Opens the "Add Existing Page" modal AND refreshes the group's page grid
   * afterwards. When omitted, falls back to just opening the modal — but then
   * a newly added Page won't appear until the user reloads, so callers that can
   * refresh should always pass this.
   */
  onAddExistingPages?: () => void;
  /** Whether the current user may add/remove existing Pages (backend `groups.update`). */
  canManagePages?: boolean;
  /** Whether the current user may create a brand-new Page in this group (`create inkinds`). */
  canCreatePage?: boolean;
} & UIComponent;

function InkindGroupEmptyViewComp({
  group,
  search,
  onAddExistingPages,
  canManagePages = false,
  canCreatePage = false,
  className,
  ...otherProps
}: InkindGroupEmptyViewProps): React.ReactElement {
  const bem = useBemCN('inkind-group-empty-view');

  // Group managers can create Pages too, so either privilege unlocks "Create".
  const canCreate = canCreatePage || canManagePages;

  function renderView() {
    if (search) {
      return <div>No results for {search}</div>;
    }

    // No page-management privileges at all → visitors/plain members just get a
    // prompt to reach out, with no editing affordances.
    if (!canCreate) {
      return (
        <>
          <div className="rounded-full bg-secondary-100 w-21 h-21 flex items-center justify-center mx-auto mb-2 p-3">
            {/* <UsersIcon className="w-8 h-8" /> */}
            <GroupsIcon />
          </div>
          <div {...bem('title')}>No support Pages have been added yet.</div>
          <div {...bem('content')}>Check back again shortly.</div>
        </>
      );
    }

    return (
      <>
        <div className="rounded-full bg-secondary-100 w-21 h-21 flex items-center justify-center mx-auto mb-2 p-3">
          {/* <UsersIcon className="w-8 h-8" /> */}
          <GroupsIcon />
        </div>
        <div {...bem('title', undefined, 'text-xl')}>No InKind Pages in this group yet</div>
        <div {...bem('content')}>
          <div {...bem('member-description', undefined, 'text-neutral-700 text-md leading-loose mb-4 mt-1')}>
            Add an existing InKind Page you organize &mdash; or start a brand-new
            <br /> one so your group can rally around it together.
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-center items-stretch sm:items-center">
          {canCreate && (
            <Button
              type={'a'}
              href={routes.create() + `?${createPageGroupIdParameter}=${group.id}`}
              variant="secondary"
              prepend={<DocumentIcon />}
            >
              Create new InKind Page
            </Button>
          )}

          {canManagePages && (
            <Button
              type="button"
              onClick={() => (onAddExistingPages ? onAddExistingPages() : openAddInkindPagesModal(group))}
              variant="secondary-outline"
              prepend={<DocumentAddIcon />}
            >
              Add an Existing Page
            </Button>
          )}
        </div>
      </>
    );
  }

  return (
    <div {...bem(undefined, undefined, className)} {...otherProps}>
      <div {...bem('content-wrapper')}>{renderView()}</div>
    </div>
  );
}

export const InkindGroupEmptyView = withComponentErrorBoundary(InkindGroupEmptyViewComp);
